/*
  TYOHAAR announcement ticker — the mobile announcement bar.

  WHY THIS EXISTS
    The bar used Dawn's scroll-snap slider on a phone. That slider is built for
    a row of cards you can swipe; for one line of text it has two problems you
    cannot style away. Each announcement arrives from the right, and when the
    last one wraps back to the first the whole track jumps the other way — so
    the movement reverses once a cycle for no reason the reader can see. And a
    horizontal slide across a thirty-pixel bar is a lot of travel for four
    words.

    This replaces it with a cross-fade: the line leaving drifts up and out, the
    line arriving rises into its place. Always the same direction, no wrap to
    give away, and the eye reads text that settles rather than text that slides
    past.

  HOW IT HOLDS ITS HEIGHT
    Every announcement sits in the same CSS grid cell, so the bar is as tall as
    the tallest one and never changes height as they cycle. Absolute
    positioning would collapse it to nothing.

  WHAT IT DOES NOT DO
    Announce itself. All the announcements are in the DOM and a screen reader
    reads them in order once, which is the whole message — a live region would
    interrupt whatever was being read every few seconds instead.
*/

class TyohaarTicker extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-ticker-item]'));
    if (this.items.length < 2) return;

    this.hold = (Number(this.dataset.hold) || 5) * 1000;
    this.index = 0;
    this.timer = null;
    this.paused = false;

    this.items.forEach((el, i) => el.classList.toggle('is-current', i === 0));

    // Reduced motion keeps the rotation — the announcements still need to be
    // read — but swaps them without the drift. Removing the movement is the
    // request; removing the information is not.
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) this.classList.add('is-still');

    this.bind();
    this.watch();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  bind() {
    // A reader who has stopped on it is reading it. Touch counts: on a phone
    // a finger resting on the bar is the same intent as a cursor.
    const pause = () => this.pause(true);
    const resume = () => this.pause(false);
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.addEventListener('touchstart', pause, { passive: true });
    this.addEventListener('touchend', resume, { passive: true });

    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  watch() {
    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      if (this.visible && !document.hidden) this.start();
      else this.stop();
    });
    this.observer.observe(this);
    this.visible = true;
    this.start();
  }

  start() {
    if (this.timer || this.paused) return;
    this.timer = setInterval(() => this.next(), this.hold);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  pause(on) {
    this.paused = on;
    if (on) this.stop();
    else this.start();
  }

  next() {
    const from = this.items[this.index];
    this.index = (this.index + 1) % this.items.length;
    const to = this.items[this.index];

    // `is-leaving` carries the outgoing line up and out. It is cleared on the
    // transition it started rather than on a timer, so the two can never drift
    // apart if a frame is dropped.
    from.classList.remove('is-current');
    from.classList.add('is-leaving');
    to.classList.add('is-current');

    const done = () => {
      from.classList.remove('is-leaving');
      from.removeEventListener('transitionend', done);
    };
    from.addEventListener('transitionend', done);
    // A belt for the case where the element is hidden mid-transition and the
    // event never fires at all.
    setTimeout(done, 1200);
  }
}

customElements.define('tyoh-ticker', TyohaarTicker);
